import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useDevFillMetricsMutation } from "../../store/apis/adminApi";

// True only under `yarn start` (react-scripts dev server). `react-scripts
// build` always sets NODE_ENV to "production", so this is a constant `false`
// in every build and the minifier drops everything guarded by it.
export const IS_DEV = process.env.NODE_ENV === "development";

// Dev-only overlay: a deliberately ugly grey "DEV" tag in the top-left corner
// plus a tight, hard grey edge fading inward around the whole screen, so a
// local tab is never mistaken for production. The edge is click-through; the
// tag opens a dev tools dropdown styled on purpose to clash with the app's
// theme, so nobody mistakes it for a real feature. Every 5s the edge
// "breathes" (swells inward and settles back) to catch the eye.
const EDGE_REST =
  "inset 0 0 0 2px rgba(80, 80, 80, 0.9), inset 0 0 10px 3px rgba(80, 80, 80, 0.85)";
const EDGE_PEAK =
  "inset 0 0 0 3px rgba(80, 80, 80, 0.95), inset 0 0 40px 12px rgba(80, 80, 80, 0.8)";

// Paleta y tipografía a propósito chillonas y ajenas al tema de la app.
const UGLY_FONT = '"Comic Sans MS", "Comic Sans", "Chalkboard SE", cursive';
const LIME = "#39ff14";
const MAGENTA = "#ff00cc";
const YELLOW = "#fff200";
const BLACK = "#000";

type Feedback =
  | { kind: "ok"; text: string; reload: boolean }
  | { kind: "error"; text: string };

const DevTools: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [fillMetrics, { isLoading }] = useDevFillMetricsMutation();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleFill = async () => {
    setFeedback(null);
    try {
      const { data } = await fillMetrics().unwrap();
      const rows =
        data.rows.internalCampaignMetrics +
        data.rows.insertionOrderMetrics +
        data.rows.lineItemMetrics +
        data.rows.campaignMetrics;
      setFeedback(
        rows === 0
          ? { kind: "ok", text: t("devTools.nothing", { to: data.to }), reload: false }
          : {
              kind: "ok",
              text: t("devTools.done", {
                rows,
                campaigns: data.internalCampaigns,
                from: data.from,
                to: data.to,
              }),
              reload: true,
            }
      );
    } catch (err) {
      const e = err as { data?: { message?: string }; status?: number | string };
      setFeedback({
        kind: "error",
        text: t("devTools.error", {
          message: e?.data?.message ?? String(e?.status ?? "?"),
        }),
      });
    }
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        position: "fixed",
        top: 6,
        left: 6,
        zIndex: 100000,
        fontFamily: UGLY_FONT,
        "@media print": { display: "none" },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          px: 0.75,
          py: 0.25,
          backgroundColor: "#8a8a8a",
          color: "#e6e6e6",
          border: "2px dashed #555",
          fontFamily: '"Courier New", monospace',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 2,
          lineHeight: 1,
          transform: "rotate(-4deg)",
          userSelect: "none",
          cursor: "pointer",
          "&:hover": { backgroundColor: MAGENTA, color: YELLOW },
        }}
      >
        DEV {open ? "▲" : "▼"}
      </Box>

      {open && (
        <Box
          role="menu"
          sx={{
            mt: 1,
            width: 290,
            backgroundColor: LIME,
            color: BLACK,
            border: `4px ridge ${MAGENTA}`,
            boxShadow: `6px 6px 0 ${BLACK}, 10px 10px 0 ${MAGENTA}`,
            transform: "rotate(1.5deg)",
            backgroundImage: `repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,0,204,0.12) 10px 20px)`,
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.5,
              backgroundColor: MAGENTA,
              color: YELLOW,
              fontWeight: 900,
              fontSize: 14,
              textAlign: "center",
              textShadow: `2px 2px 0 ${BLACK}`,
              borderBottom: `3px dotted ${BLACK}`,
            }}
          >
            {t("devTools.menuTitle")}
          </Box>

          <Box
            component="button"
            type="button"
            role="menuitem"
            onClick={handleFill}
            disabled={isLoading}
            sx={{
              display: "block",
              width: "calc(100% - 16px)",
              m: 1,
              p: 1,
              fontFamily: UGLY_FONT,
              fontSize: 14,
              fontWeight: 700,
              textAlign: "left",
              color: YELLOW,
              backgroundColor: BLACK,
              border: `3px outset ${YELLOW}`,
              cursor: isLoading ? "wait" : "pointer",
              "&:hover:not(:disabled)": {
                backgroundColor: YELLOW,
                color: MAGENTA,
                borderStyle: "inset",
              },
              "&:disabled": { opacity: 0.7 },
            }}
          >
            {isLoading
              ? `⏳ ${t("devTools.running")}`
              : `🔥 ${t("devTools.fillMetrics")}`}
            <Box
              component="span"
              sx={{ display: "block", fontSize: 11, fontWeight: 400, mt: 0.5 }}
            >
              {t("devTools.fillMetricsHint")}
            </Box>
          </Box>

          {feedback && (
            <Box
              sx={{
                mx: 1,
                mb: 1,
                p: 1,
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: feedback.kind === "ok" ? YELLOW : "#ff2a2a",
                color: BLACK,
                border: `2px dashed ${BLACK}`,
              }}
            >
              {feedback.text}
              {feedback.kind === "ok" && feedback.reload && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => window.location.reload()}
                  sx={{
                    display: "block",
                    mt: 0.75,
                    fontFamily: UGLY_FONT,
                    fontWeight: 700,
                    backgroundColor: MAGENTA,
                    color: YELLOW,
                    border: `2px solid ${BLACK}`,
                    cursor: "pointer",
                  }}
                >
                  ↻ {t("devTools.reload")}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const DevIndicator: React.FC = () =>
  IS_DEV ? (
    <>
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 99999,
          boxShadow: EDGE_REST,
          "@keyframes devEdgeBreathe": {
            "0%, 60%, 100%": { boxShadow: EDGE_REST },
            "80%": { boxShadow: EDGE_PEAK },
          },
          animation: "devEdgeBreathe 5s ease-in-out infinite",
          "@media print": { display: "none" },
        }}
      />
      <DevTools />
    </>
  ) : null;

export default DevIndicator;
